import React, { useState, useEffect } from 'react';
import { List, RefreshCw, AlertCircle, Search, CheckCircle, XCircle } from 'lucide-react';
import { apiService } from '../../services/apiService';

function formatDuration(seconds) {
  if (seconds == null) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const min = Math.floor(seconds / 60);
  const sec = Math.round(seconds % 60);
  return sec > 0 ? `${min}m ${sec}s` : `${min}m`;
}

function formatDate(isoStr) {
  if (!isoStr) return '—';
  try {
    const d = new Date(isoStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch {
    return isoStr;
  }
}

function ConfidenceDot({ score }) {
  if (score == null) return <span className="text-slate-600">—</span>;
  const color = score >= 0.81 ? 'bg-emerald-400' : score >= 0.6 ? 'bg-amber-400' : 'bg-red-400';
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`w-2 h-2 rounded-full ${color}`} />
      <span className="text-slate-300">{(score * 100).toFixed(0)}%</span>
    </span>
  );
}

function BoolBadge({ value, trueLabel = 'Yes', falseLabel = 'No' }) {
  if (value) {
    return <span className="text-amber-400 text-xs">{trueLabel}</span>;
  }
  return <span className="text-slate-600 text-xs">{falseLabel}</span>;
}

export default function SessionLogTab() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');

  async function loadData() {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.fetchDashboardView('SessionDetail', { limit: 200 });
      setSessions(res.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadData(); }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-amber-400 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-center gap-3">
        <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
        <div>
          <p className="text-red-400 font-medium">Failed to load session data</p>
          <p className="text-red-400/70 text-sm">{error}</p>
        </div>
        <button onClick={loadData} className="ml-auto px-3 py-1.5 text-sm bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors">
          Retry
        </button>
      </div>
    );
  }

  const filtered = filter
    ? sessions.filter(s =>
        (s.ExtPoNumber || '').toLowerCase().includes(filter.toLowerCase()) ||
        (s.UserEmail || '').toLowerCase().includes(filter.toLowerCase()) ||
        (s.FileName || '').toLowerCase().includes(filter.toLowerCase()) ||
        (s.ExtCustomerName || '').toLowerCase().includes(filter.toLowerCase()) ||
        (s.CorrelationId || '').toLowerCase().includes(filter.toLowerCase())
      )
    : sessions;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <List className="w-5 h-5 text-amber-400" />
          <h2 className="text-lg font-semibold text-white">Session Log</h2>
          <span className="text-sm text-slate-500">({filtered.length} sessions)</span>
        </div>
        <button onClick={loadData} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors">
          <RefreshCw className="w-3.5 h-3.5" />
          Refresh
        </button>
      </div>

      {/* Search */}
      <div className="relative w-full max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <input
          type="text"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder="Filter by PO #, email, file name, customer..."
          className="w-full pl-9 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-amber-400/50"
        />
      </div>

      {/* Session Table */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
        {filtered.length === 0 ? (
          <p className="text-slate-500 text-sm py-8 text-center">No sessions found</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700 bg-slate-800/80">
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Date</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">PO #</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">File</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">User</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Customer</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Items</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">ShipTo Conf</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Item Conf</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Changed</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Review Time</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Cust Changed</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">ShipTo Changed</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">A+ Order</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s, i) => (
                  <tr key={i} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                    <td className="py-2 px-3 text-slate-400 text-xs whitespace-nowrap">{formatDate(s.CreatedAt)}</td>
                    <td className="py-2 px-3 text-white font-medium">{s.ExtPoNumber || '—'}</td>
                    <td className="py-2 px-3 text-slate-300 text-xs max-w-[150px] truncate" title={s.FileName}>{s.FileName || '—'}</td>
                    <td className="py-2 px-3 text-slate-400 text-xs">{s.UserEmail || '—'}</td>
                    <td className="py-2 px-3 text-slate-300 text-xs max-w-[150px] truncate" title={s.ExtCustomerName}>{s.ExtCustomerName || '—'}</td>
                    <td className="py-2 px-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                        s.Status === 'submitted' ? 'bg-emerald-500/10 text-emerald-400' :
                        s.Status === 'reviewing' ? 'bg-amber-500/10 text-amber-400' :
                        s.Status === 'failed' ? 'bg-red-500/10 text-red-400' :
                        'bg-slate-500/10 text-slate-400'
                      }`}>
                        {s.Status === 'submitted' && <CheckCircle className="w-3 h-3" />}
                        {s.Status === 'failed' && <XCircle className="w-3 h-3" />}
                        {s.Status}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-slate-300">{s.ExtItemCount ?? '—'}{s.SubItemCount != null && s.SubItemCount !== s.ExtItemCount ? ` → ${s.SubItemCount}` : ''}</td>
                    <td className="py-2 px-3"><ConfidenceDot score={s.ShipToConfidence} /></td>
                    <td className="py-2 px-3"><ConfidenceDot score={s.OverallItemConfidence} /></td>
                    <td className="py-2 px-3 text-slate-300">{s.ItemsChanged ?? '—'}</td>
                    <td className="py-2 px-3 text-slate-300">{formatDuration(s.ReviewDurationSeconds)}</td>
                    <td className="py-2 px-3 text-slate-300">{s.TotalCsrActions ?? '—'}</td>
                    <td className="py-2 px-3"><BoolBadge value={s.CustomerChanged} /></td>
                    <td className="py-2 px-3"><BoolBadge value={s.ShipToChanged} /></td>
                    <td className="py-2 px-3 text-slate-400 font-mono text-xs">{s.ErpOrderNumber || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
